import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TableTopBar from "./TableTopBar";
import TableSettings from "./TableSettings";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Socket } from "socket.io-client";
import { ReplayControls } from "../ReplayControls";
import { useGameTableLogic } from "./gameTableLogic";
import { createDebugTable } from "./gameTableFunctions";
import { PlayerSelector } from "./PlayerSelector";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { RuleLinks } from "./RuleLinks";
import "../Table.css";
import { makeTableContext, TableContext } from "../tableContext";
import { GameLog } from "./GameLog";
import "./TableContainer.css";
import { GameSituationContainer } from "./GameSituationContainer";
import { GameStatusBar } from "./GameStatusBar";
import { GameChat } from "./GameChat";
import { AllStats, MessageData } from "src/components/types";
import { GameChatPopup } from "./GameChatPopup";
import { EndgameRedirectDialog } from "./EndgameRedirectDialog";
import { AlertPopup } from "./AlertPopup";
import { OverviewCard } from "./overviewCard";
import { TableJoinFailedDialog } from "./TableJoinFailedDialog";
import { importAll, isSafari } from "../tableUtility";
import ResultsFeedback, { ResultsFeedbackRefs } from "src/components/gametable/redesign/ResultsFeedback";
import TableTile from "src/components/TableTile";
import { canAdminProject, canSeeFeedback } from "src/components/utils";
import IngameReportForm from "src/components/gametable/IngameReportForm";
import { SafariWarningDialog } from "src/components/gametable/redesign/SafariWarningDialog";

export type TableContainerProps = {
  socket: Socket;
  connected: boolean;
  topBarAsButton: ReactNode;
  reconnectAttempt?: number;
};

function TableContainer({ socket, connected, topBarAsButton, reconnectAttempt = 0 }: TableContainerProps) {
  let params = useParams();
  let query = useSearchParams()[0];
  const navigate = useNavigate();

  const [scaleAdjust, setScaleAdjust] = useState<number>(+(localStorage.getItem("zoom-" + params.projectSlug) ?? 100));
  const lastScaleRatio = useRef<number>(1);
  const lastZoomDiff = useRef<number>(0);
  const [globalVolume, setGlobalVolume] = useState<number>(+(localStorage.getItem("global-volume") ?? 0.5));
  const [evalSpeed, setEvalSpeed] = useState<number>(+(localStorage.getItem("evalSpeed-" + params.projectSlug) ?? 1));
  const [deckReloadStatus, setDeckReloadStatus] = useState<string>("");
  const [dataSheetReloadStatus, setDataSheetReloadStatus] = useState<string>("");
  let [images, setImages] = useState<{
    [key: string]: string | { default: string };
  } | null>(null);

  let color = query.get("playAsColor") ?? localStorage.getItem("play-as-color-" + params.tableId);
  const [playAsColor, setPlayAsColor] = useState(color ?? "spectator");
  const [canSelectColor, setCanSelectColor] = useState(true);

  let situationMousePos = useRef({ x: 0, y: 0 });
  let physicalMousePos = useRef({ x: 0, y: 0 });
  const [mouseChanged, setMouseChanged] = useState(0);

  const [chatOpen, setChatOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [openDeckContentId, setOpenDeckContentId] = useState<string | null>(null);
  const deckContentSlotRef = useRef<HTMLDivElement>(null);

  const [chatMessages, setChatMessages] = useState<MessageData[]>([]);
  const [popupMessage, setPopupMessage] = useState<MessageData | undefined>();

  const [allStats, setAllStats] = useState<AllStats>({ playerStats: {}, tableStats: {}, objectStats: {} });

  const [isEndgameRedirectDialogOpen, setIsEndgameRedirectDialogOpen] = useState(false);

  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertColor, setAlertColor] = useState<string>("");
  const [alertCheatable, setAlertCheatable] = useState<boolean>(false);

  const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);
  const [zoomLensActive, setZoomLensActive] = useState<boolean>(false);

  const [tableJoinFailedDialogReason, setTableJoinFailedDialogReason] = useState<string | null>(null);
  const [zoomScale, setZoomScale] = useState(2.5);

  const [isSafariWarningDialogOpen, setIsSafariWarningDialogOpen] = useState(false);

  const resultsFeedbackRef = useRef<ResultsFeedbackRefs>(null);

  const gameTableLogic = useGameTableLogic({
    params,
    query,
    socket,
    scaleAdjust,
    evalSpeed,
    setDeckReloadStatus,
    setDataSheetReloadStatus,
    playAsColor,
    setPlayAsColor,
    setCanSelectColor,
    setChatMessages,
    setPopupMessage,
    setAllStats,
    setAlert,
    setTableJoinFailedDialogReason,
  });

  const getDisplayTooltipsValue = () => {
    if (localStorage.getItem("display-tooltips:" + params.projectSlug)) {
      return localStorage.getItem("display-tooltips:" + params.projectSlug) === "1" ? true : false;
    } else {
      return localStorage.getItem("display-tooltips-global") === "1" ? true : false;
    }
  };

  const [displayTooltips, setDisplayTooltips] = useState(getDisplayTooltipsValue);

  function toggleZoomLens(newValue: boolean) {
    setZoomLensActive(newValue);
  }

  useEffect(() => {
    const updateScrollbarWidth = () => {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
    };
    window.addEventListener("resize", updateScrollbarWidth);
    window.addEventListener("scroll", updateScrollbarWidth);
    const observer = new MutationObserver(updateScrollbarWidth);
    observer.observe(document.body, { childList: true, subtree: true });
    updateScrollbarWidth();
    return () => {
      window.removeEventListener("resize", updateScrollbarWidth);
      window.removeEventListener("scroll", updateScrollbarWidth);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (
      gameTableLogic.tableStatus == "ended" &&
      !gameTableLogic.tableData?.userAnswers &&
      !isEndgameRedirectDialogOpen
    ) {
      setIsEndgameRedirectDialogOpen(true);
    }
    if (gameTableLogic.tableStatus == "ended" && query.get("turnNumber")) {
      let turnNumberParam = query.get("turnNumber");
      if (turnNumberParam !== null && !Number.isNaN(+turnNumberParam)) {
        gameTableLogic.moveToEvent(+turnNumberParam);
      }
    }
  }, [gameTableLogic.tableStatus]);

  // ugly hack to fix firefox not properly accounting for right negative margin on initial page load
  useEffect(() => {
    if (imagesLoaded) {
      setTimeout(() => {
        setScaleAdjust((a) => a + 0.001);
        setTimeout(() => {
          setScaleAdjust((a) => a - 0.001);
        }, 100);
      }, 2000);
    }
  }, [imagesLoaded]);

  useEffect(() => {
    if (gameTableLogic.tableStatus == "ended") {
      navigate("?turnNumber=" + gameTableLogic.currEventNumber);
    }
  }, [gameTableLogic.tableStatus, gameTableLogic.currEventNumber]);

  const addGameIconsToText = useCallback(
    (text: string, options?: { [key: string]: any }) => {
      if (typeof text !== "string" || text.trim().length === 0) {
        return "";
      }
      let childData = [];

      var lastClose = -1;
      let lastOpen = text.length;
      let iconList = gameTableLogic.tableData?.projectInfo.globalAssets;
      for (let c in [...text]) {
        if (text[c] === "[") {
          lastOpen = +c;
          let displayText = text.substring(lastClose + 1, +c);
          childData.push({ type: "text", text: displayText });
        }
        if (text[c] === "\n") {
          let displayText = text.substring(lastClose + 1, +c);
          childData.push({ type: "text", text: displayText });
          lastClose = +c;
          childData.push({ type: "linebreak" });
        }
        if (text[c] === "]") {
          lastClose = +c;
          let iconName = text.substring(lastOpen + 1, lastClose).replace(/ /g, "");
          if (iconList && iconList[iconName]) {
            childData.push({
              type: "icon",
              name: iconName,
              imgUrl: `url(${iconList[iconName].url})`,
            });
          } else {
            childData.push({ type: "iconfallback", text: iconName });
          }
        } else if (+c === text.length - 1) {
          let displayText = text.substring(lastClose + 1);
          childData.push({ type: "text", text: displayText });
        }
      }
      let children = [];
      let i = 0;
      for (let data of childData) {
        switch (data.type) {
          case "icon":
            children.push(
              <div
                className={"counter-icon icon-" + data.name + " " + (options?.className ?? "")}
                key={"iconized-text-counter-icon-" + data.name + i}
                style={{
                  backgroundImage: data.imgUrl,
                }}
              ></div>,
            );
            break;
          case "iconfallback":
            children.push(
              <span key={"iconized-text-text-" + i++} className="icon-fallback">
                {data.text}
              </span>,
            );
            break;
          case "text":
            children.push(
              ...(data.text ?? "")
                .split(" ")
                .filter((t) => t.length > 0)
                .map((t, j, a) => (
                  <span
                    key={"iconized-text-message-text-" + i + ":" + j}
                    className={options?.className ?? ""}
                    style={options?.style ?? {}}
                  >
                    {(j == 0 && (data.text ?? "")[0] === " " ? " " : "") + t + (j === a.length - 1 ? "" : " ")}
                  </span>
                )),
            );
            break;
          case "linebreak":
            children.push(
              <span
                key={"iconized-text-linebreak-" + i++}
                className="linebreak"
                style={{ display: "block", width: "100%", height: "0px" }}
              ></span>,
            );
            break;
          default:
        }
        ++i;
      }
      return children;
    },
    [gameTableLogic.getImage, gameTableLogic.tableData?.projectInfo.globalAssets],
  );

  const handleMouseMove = useCallback(
    (evt: React.MouseEvent) => {
      let situationDiv = document.querySelector(".game-situation:not(.zoom-lens)")?.getBoundingClientRect();
      let situationScale = gameTableLogic.getTargetScale();
      if (situationDiv && situationScale) {
        let x = (evt.clientX - situationDiv.left) / situationScale;
        let y = (evt.clientY - situationDiv.top) / situationScale;
        if (situationMousePos) {
          situationMousePos.current = { x, y };
        }
        if (physicalMousePos) {
          physicalMousePos.current = { x: evt.clientX, y: evt.clientY };
        }
      }
      setMouseChanged((m) => m + 1);
    },
    [situationMousePos, scaleAdjust],
  );

  const containerCreateDebugTable = useCallback(
    (evtNumber: number) => {
      createDebugTable(params.tableId, evtNumber, navigate);
    },
    [params.tableId, navigate],
  );

  const [edgeScroll, setEdgeScroll] = useState(() => {
    const stored = localStorage.getItem(`edgeScroll-${params.projectSlug}`);
    return stored === null ? false : stored === "true";
  });

  useEffect(() => {
    let animationFrame = 0;
    let lastScroll = { x: 0, y: 0 };

    function onMouseMove(e: MouseEvent) {
      if (!edgeScroll) return;

      const edgeThreshold = 100;
      const fastEdgeThreshold = 30;
      const scrollSpeed = 10;
      const fastScrollSpeed = 20;

      let scrollX = 0;
      let scrollY = 0;

      if (e.clientX < fastEdgeThreshold) {
        scrollX = -fastScrollSpeed;
      } else if (e.clientX < edgeThreshold) {
        scrollX = -scrollSpeed;
      } else if (e.clientX > window.innerWidth - fastEdgeThreshold) {
        scrollX = fastScrollSpeed;
      } else if (e.clientX > window.innerWidth - edgeThreshold) {
        scrollX = scrollSpeed;
      }
      let situationY = e.clientY - 55; // table top bar

      if (situationY < fastEdgeThreshold) {
        scrollY = -fastScrollSpeed;
      } else if (situationY < edgeThreshold) {
        scrollY = -scrollSpeed;
      } else if (situationY > window.innerHeight - fastEdgeThreshold) {
        scrollY = fastScrollSpeed;
      } else if (situationY > window.innerHeight - edgeThreshold) {
        scrollY = scrollSpeed;
      }
      if (situationY < 0) {
        scrollX = scrollY = 0; // table top bar
      }

      const scrollChanged = scrollX !== lastScroll.x || scrollY !== lastScroll.y;

      if (scrollChanged) {
        lastScroll = { x: scrollX, y: scrollY };
        cancelAnimationFrame(animationFrame);

        const scroll = () => {
          const el = document.scrollingElement || document.documentElement;
          el.scrollLeft += lastScroll.x;
          el.scrollTop += lastScroll.y;
          animationFrame = requestAnimationFrame(scroll);
        };

        scroll();
      }
    }

    window.addEventListener("mousemove", onMouseMove);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [edgeScroll]);

  useEffect(() => {
    if (!zoomLensActive) return;

    function onZoomLensMouseMove(e: MouseEvent) {
      const situationDiv = document.querySelector(".game-situation:not(.zoom-lens)")?.getBoundingClientRect();
      const situationScale = gameTableLogic.getTargetScale();
      if (situationDiv && situationScale) {
        situationMousePos.current = {
          x: (e.clientX - situationDiv.left) / situationScale,
          y: (e.clientY - situationDiv.top) / situationScale,
        };
        physicalMousePos.current = { x: e.clientX, y: e.clientY };
      }
      setMouseChanged((m) => m + 1);
    }

    window.addEventListener("mousemove", onZoomLensMouseMove);
    return () => window.removeEventListener("mousemove", onZoomLensMouseMove);
  }, [zoomLensActive]);

  function changeScaleAdjust(newValue: number) {
    lastScaleRatio.current = newValue / scaleAdjust;
    if (newValue > 98 && newValue < 102) {
      newValue = 100;
    }
    setScaleAdjust(newValue);
    localStorage.setItem("zoom-" + params.projectSlug, "" + newValue);
  }

  const fixScrolling = useEffect(() => {
    let windowHeight = window.innerHeight;
    let windowWidth = gameTableLogic.windowWidth;
    let containerWidth = document.querySelector(".game-situation")?.clientWidth ?? 0;
    let containerHeight = document.querySelector(".game-situation")?.clientHeight ?? 0;
    let situationScale = gameTableLogic.getTargetScale();
    let situationMousePosition = { x: situationMousePos.current.x, y: situationMousePos.current.y };
    let ratio = Math.pow(1 + windowWidth / containerWidth / 10, lastZoomDiff.current / 10); // I have no idea where this magic number came from, but it seems to work
    if (lastScaleRatio.current < 1) {
      ratio = 1 / ratio;
    }
    let scaledMousePosition = {
      x: situationMousePosition.x * ratio,
      y: situationMousePosition.y * ratio,
    };
    let mouseDiff = {
      x: +scaledMousePosition.x - situationMousePosition.x,
      y: +scaledMousePosition.y - situationMousePosition.y,
    };
    window.scrollTo({
      left: window.scrollX + mouseDiff.x,
      top: window.scrollY + mouseDiff.y,
      behavior: "instant",
    });
  }, [scaleAdjust]);

  function changeGlobalVolume(newValue: number) {
    setGlobalVolume(newValue);
    localStorage.setItem("global-volume", "" + newValue);
  }

  function changeEvalSpeed(newValue: number) {
    setEvalSpeed(newValue);
    localStorage.setItem("evalSpeed-" + params.projectSlug, "" + newValue);
  }

  function handleChatToggled() {
    setChatOpen((a) => !a);
    setPopupMessage(undefined);
  }

  function setAlert(message: string, color: string, cheatable: boolean = false) {
    setAlertCheatable(cheatable);
    setAlertMessage(message);
    setAlertColor(color);
  }

  function clearAlert() {
    setAlertMessage("");
    setAlertColor("");
    setAlertCheatable(false);
  }

  function openChat() {
    setChatOpen(true);
    setPopupMessage(undefined);
  }

  const scrollToResults = useCallback(() => {
    setIsEndgameRedirectDialogOpen(false);
    resultsFeedbackRef.current?.resultsRefEl?.scrollIntoView({ behavior: "smooth" });
  }, [setIsEndgameRedirectDialogOpen]);

  const scrollToFeedback = useCallback(() => {
    setIsEndgameRedirectDialogOpen(false);
    resultsFeedbackRef.current?.feedbackRefEl?.scrollIntoView({ behavior: "smooth" });
  }, [setIsEndgameRedirectDialogOpen]);

  function increaseScaleAdjust(increaseBy: number) {
    lastZoomDiff.current = increaseBy;
    if (scaleAdjust + increaseBy <= 600) {
      changeScaleAdjust(scaleAdjust + increaseBy);
    }
  }

  function decreaseScaleAdjust(decreaseBy: number) {
    lastZoomDiff.current = decreaseBy;
    if (scaleAdjust - decreaseBy >= 25) {
      changeScaleAdjust(scaleAdjust - decreaseBy);
    }
  }

  function changeZoomLensScale(delta: number) {
    setZoomScale((oldScale) => Math.max(1, oldScale - delta / 800));
  }

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const loadImages = async () => {
      if (!params.projectSlug) return;

      // Load images asynchronously
      const images = await importAll(params.projectSlug);
      setImages(images);
      if (setImagesLoaded) {
        setImagesLoaded(true);
      }

      console.log("Images loaded", images);
    };
    loadImages();
  }, [params.projectSlug]);

  useEffect(() => {
    if (
      localStorage.getItem("hide-safari-warning") !== "true" &&
      gameTableLogic.tableLoaded &&
      Object.entries(gameTableLogic.decks ?? {}).length > 20 // a "dumb" way of guessing a game is too big for safari
    ) {
      setIsSafariWarningDialogOpen(isSafari());
    }
  }, [params.projectSlug, gameTableLogic.tableLoaded]);

  let zoomLensContentEl = useMemo(() => {
    return (
      <GameSituationContainer
        displayTooltips={false}
        addGameIconsToText={addGameIconsToText}
        statusBarStatus={connected ? gameTableLogic.statusBarStatus : "disconnected"}
        isZoomLens={zoomScale}
        images={images}
      />
    );
  }, [gameTableLogic.tableStateRef, images, zoomScale, scaleAdjust, gameTableLogic.statusBarStatus]);

  let mainContentEl = useMemo(() => {
    if (gameTableLogic.tableLoaded) {
      return (
        <GameSituationContainer
          statusBarStatus={connected ? gameTableLogic.statusBarStatus : "disconnected"}
          outOfSync={gameTableLogic.outOfSync}
          invitePwd={gameTableLogic.tableData?.invitePwd ?? ""}
          addGameIconsToText={addGameIconsToText}
          moveToEvent={gameTableLogic.moveToEvent}
          currEventNumber={gameTableLogic.currEventNumber}
          toggleLog={() => setLogOpen((a) => !a)}
          openChat={openChat}
          setImagesLoaded={setImagesLoaded}
          increaseScaleAdjust={increaseScaleAdjust}
          decreaseScaleAdjust={decreaseScaleAdjust}
          changeZoomLensScale={changeZoomLensScale}
          displayTooltips={displayTooltips}
          images={images}
        />
      );
    }
    return <></>;
  }, [
    gameTableLogic.tableStateRef,
    gameTableLogic.tableLoaded,
    images,
    gameTableLogic.currEventNumber,
    scaleAdjust,
    gameTableLogic.statusBarStatus,
    gameTableLogic.decks,
  ]);

  let bestRole = "";
  const roleInfo = gameTableLogic.tableData?.projectInfo?.roles as { [key: string]: string[] };
  for (let role in roleInfo) {
    if (roleInfo[role]?.includes(localStorage.getItem("user-name") ?? "")) {
      bestRole = role;
    }
  }

  const settingsEl = (
    <TableSettings
      socket={socket}
      projectRole={bestRole}
      scaleAdjust={scaleAdjust}
      changeScaleAdjust={changeScaleAdjust}
      globalVolume={globalVolume}
      changeGlobalVolume={changeGlobalVolume}
      evalSpeed={evalSpeed}
      changeEvalSpeed={changeEvalSpeed}
      deckReloadStatus={deckReloadStatus}
      setDeckReloadStatus={setDeckReloadStatus}
      dataSheetReloadStatus={dataSheetReloadStatus}
      setDataSheetReloadStatus={setDataSheetReloadStatus}
      editMode={gameTableLogic.editMode}
      setEditMode={gameTableLogic.setEditMode}
      edgeScroll={edgeScroll}
      setEdgeScroll={setEdgeScroll}
      cheatMode={gameTableLogic.cheatMode}
      setCheatMode={gameTableLogic.setCheatMode}
      displayTooltips={displayTooltips}
      setDisplayTooltips={setDisplayTooltips}
    />
  );

  const replayEl = (
    <ReplayControls
      moveToEvent={gameTableLogic.moveToEvent}
      allEvents={gameTableLogic.allEventsRef.current}
      currEventNumber={gameTableLogic.currEventNumber}
      createDebugTable={() => createDebugTable(params.tableId, gameTableLogic.currEventNumber, navigate)}
      tableStatus={gameTableLogic.tableStatus}
    />
  );

  const playerSelectorEl = (
    <PlayerSelector
      canSelectColor={canSelectColor}
      playAsColor={playAsColor}
      players={gameTableLogic.tableStateRef.current?.players}
      changeCurrentPlayer={gameTableLogic.changeCurrentPlayer}
    />
  );

  const ruleLinksEl = <RuleLinks rules={gameTableLogic.tableData?.projectInfo.rules} />;

  let statusBarEl = <></>;
  if (gameTableLogic.tableLoaded) {
    let status = gameTableLogic.statusBarStatus;
    if (!connected) {
      status = "disconnected";
    }
    if (gameTableLogic.outOfSync) {
      status = "outOfSync";
    }
    statusBarEl = (
      <GameStatusBar
        statusBarStatus={status}
        addGameIconsToText={addGameIconsToText}
        reconnectAttempt={reconnectAttempt}
      />
    );
  }

  let lensWrap = useMemo(() => {
    if (!zoomLensActive) {
      return <></>;
    }
    let zoomLeft = situationMousePos.current.x;
    let zoomTop = situationMousePos.current.y;
    let lensWidth = 550;
    return (
      <div
        className="zoom-lens-wrap"
        style={{
          height: lensWidth * gameTableLogic.getTargetScale(),
          width: lensWidth * gameTableLogic.getTargetScale(),
          top: `${(zoomTop - lensWidth / 2) * gameTableLogic.getTargetScale()}px`,
          left: `${(zoomLeft - lensWidth / 2) * gameTableLogic.getTargetScale()}px`,
        }}
      >
        <div
          className="zoom-lens-container"
          style={{
            marginLeft: -(zoomLeft * zoomScale - lensWidth / 2) * gameTableLogic.getTargetScale() + "px",
            marginTop: -(zoomTop * zoomScale - lensWidth / 2) * gameTableLogic.getTargetScale() + "px",
          }}
        >
          {zoomLensContentEl}
        </div>
      </div>
    );
  }, [situationMousePos, mouseChanged, zoomLensActive, scaleAdjust, zoomScale]);

  const currentTableState = gameTableLogic.tableStateRef.current;
  const forceExpandedContainer: string | undefined = currentTableState?.getActiveState?.()?.forceExpandedContainer;
  useEffect(() => {
    setOpenDeckContentId(forceExpandedContainer ?? null);
  }, [currentTableState, forceExpandedContainer]);

  const tableContext = useMemo(() => {
    return makeTableContext(gameTableLogic, evalSpeed, toggleZoomLens, situationMousePos, {
      openId: openDeckContentId,
      setOpenId: setOpenDeckContentId,
      slotRef: deckContentSlotRef,
    });
  }, [gameTableLogic, evalSpeed, openDeckContentId]);

  const logEl = useMemo(() => {
    return (
      <GameLog
        messages={gameTableLogic.logMessages}
        playerColors={gameTableLogic.playerColors}
        getImage={gameTableLogic.getImage}
        logOpen={logOpen}
        currEventNumber={gameTableLogic.currEventNumber}
        tableStatus={gameTableLogic.tableStatus}
        moveToEvent={gameTableLogic.moveToEvent}
        createDebugTable={containerCreateDebugTable}
        globalAssets={gameTableLogic.globalAssets}
      />
    );
  }, [
    gameTableLogic.logMessages,
    gameTableLogic.playerColors,
    gameTableLogic.getImage,
    logOpen,
    gameTableLogic.currEventNumber,
    gameTableLogic.tableStatus,
  ]);

  const chatEl = (
    <GameChat
      eventManager={gameTableLogic.eventManager}
      messages={chatMessages}
      chatOpen={chatOpen}
      closeChat={() => {
        setChatOpen(false);
        setPopupMessage(undefined);
      }}
    />
  );

  if (gameTableLogic.tableStatus === "open") {
    let tableData = gameTableLogic.tableData;
    if (tableData) {
      return (
        <div className="table-container-lobby" data-project={params.projectSlug}>
          <TableContext.Provider value={tableContext}>
            <TableTile
              tableInfo={tableData}
              options={tableData.projectInfo.options}
              projectSlug={params.projectSlug ?? ""}
              dataSheet={tableData.projectInfo.dataSheet}
              globalAssets={tableData.projectInfo.globalAssets}
              projectName={tableData.projectInfo.project_name}
              decks={tableData.projectInfo.decks}
              socket={socket}
              tableStarted={gameTableLogic.fetchTable}
              showAllOptions={canAdminProject(tableData.projectInfo) || canSeeFeedback(tableData.projectInfo)}
            />
            <GameChat
              eventManager={gameTableLogic.eventManager}
              messages={chatMessages}
              chatOpen={true}
              closeChat={() => {}}
              isLobby={true}
            />
          </TableContext.Provider>
          <img src={`/backgrounds/${params.projectSlug}.webp`} className="project-detail-redesign-image-background" />
          <div className="project-detail-redesign-gradient-container" />
        </div>
      );
    }
    return <div>Table has not started, and no data found</div>;
  }

  if (gameTableLogic.tableLoaded) {
    return (
      <>
        <TableContext.Provider value={tableContext}>
          <div className="table-container">
            <TableTopBar
              settingsElement={settingsEl}
              replayElement={replayEl}
              playerSelectorElement={playerSelectorEl}
              ruleLinksElement={ruleLinksEl}
              chatOpen={chatOpen}
              toggleChat={handleChatToggled}
              logOpen={logOpen}
              toggleLog={() => setLogOpen((a) => !a)}
              statusBarElement={statusBarEl}
              tableStatus={gameTableLogic.tableStatus}
              chatElement={chatEl}
              logElement={logEl}
              deckContentSlotRef={deckContentSlotRef}
              cgoTopBarAsButton={topBarAsButton}
              globalMessage={tableContext.tableState?.globals?.__globalMessage}
              currEventNumber={gameTableLogic.currEventNumber}
            />
            <div className="main-content-log-container">
              <div className="main-content-container" onMouseMove={handleMouseMove}>
                {lensWrap}
                {mainContentEl}
              </div>
              {window.componentPool && window.componentPool.__overviewCard ? (
                <OverviewCard imageUrl={gameTableLogic.getImage(window.componentPool.__overviewCard, false) ?? ""} />
              ) : null}
            </div>
            <ResultsFeedback
              ref={resultsFeedbackRef}
              tableData={gameTableLogic.tableData}
              tableStatus={gameTableLogic.tableStatus}
              imagesLoaded={imagesLoaded}
              allStats={allStats}
              playerColors={gameTableLogic.playerColors ?? []}
              playAsColor={playAsColor}
              tableId={params.tableId}
              scrollToFeedback={scrollToFeedback}
              scrollToResults={scrollToResults}
            />
          </div>
        </TableContext.Provider>
        <ConfirmationDialog
          messages={gameTableLogic.dialogMessages}
          type={gameTableLogic.dialogType}
          doConfirm={gameTableLogic.processDialogConfirmed}
          doReject={gameTableLogic.processDialogRejected}
        />
        <EndgameRedirectDialog
          isOpen={isEndgameRedirectDialogOpen}
          setIsOpen={setIsEndgameRedirectDialogOpen}
          switchToFeedback={scrollToFeedback}
          switchToScoring={scrollToResults}
          allStats={allStats}
          playerColors={gameTableLogic.playerColors ?? []}
          overviewStats={gameTableLogic.tableData?.projectInfo.overviewStats}
        />
        <TableJoinFailedDialog reason={tableJoinFailedDialogReason} setReason={setTableJoinFailedDialogReason} />
        {!chatOpen ? <GameChatPopup messageData={popupMessage} /> : null}
        {alertMessage ? (
          <AlertPopup
            message={alertMessage}
            color={alertColor}
            clearAlert={clearAlert}
            confirmTurn={() => gameTableLogic.processAlertCheated()}
            cheatable={alertCheatable}
          />
        ) : null}
        <SafariWarningDialog isOpen={isSafariWarningDialogOpen} close={() => setIsSafariWarningDialogOpen(false)} />
      </>
    );
  }

  return <div>Loading table...</div>;
}

export default TableContainer;
